import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import { Home, Payment, Receipt, Inventory, Add } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { fmt, sym } from '../utils/currency';
import AttachmentsPanel from './AttachmentsPanel';

const STATUS_COLOR = { Open: 'error', Partial: 'warning', Paid: 'success', Cancelled: 'default' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const PurchaseInvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [viewInv, setViewInv]   = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/payables/invoices`, { headers })
      .then(r => setInvoices(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load invoices'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading invoices...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Receipt sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Purchase Invoices</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/payables/invoices/new')} title="New Direct Invoice"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          <Toast error={error} onClose={() => setError('')} />

          {invoices.length === 0 ? (
            <Typography>No purchase invoices yet. Post a Purchase Order to create one.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Invoice No</strong></TableCell>
                  <TableCell><strong>Order No</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Supplier Inv No</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Total ({sym()})</strong></TableCell>
                  <TableCell align="right"><strong>Paid ({sym()})</strong></TableCell>
                  <TableCell align="right"><strong>Balance ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => {
                  const balance = (inv.totalAmount || 0) - (inv.paid || 0);
                  return (
                    <TableRow key={inv.invoiceNo} hover sx={{ cursor: 'pointer' }}
                      onClick={() => setViewInv({ ...inv, balance })}>
                      <TableCell><strong>{inv.invoiceNo}</strong></TableCell>
                      <TableCell>{inv.orderNo || '-'}</TableCell>
                      <TableCell>{inv.supplierCode}</TableCell>
                      <TableCell>{inv.supplierInvoiceNo || '-'}</TableCell>
                      <TableCell>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ''}</TableCell>
                      <TableCell align="right">{(inv.totalAmount || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{(inv.paid || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{balance.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip label={inv.status} color={STATUS_COLOR[inv.status] || 'default'}
                          size="small" variant="outlined" />
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        {inv.status !== 'Paid' && inv.status !== 'Cancelled' && (
                          <Button size="small" variant="outlined" startIcon={<Payment />}
                            onClick={() => navigate('/payables/vouchers/new', { state: { invoiceNo: inv.invoiceNo, supplierCode: inv.supplierCode } })}>
                            Pay
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Invoice Card (view) ── */}
      <Dialog open={Boolean(viewInv)} onClose={() => setViewInv(null)} maxWidth="sm" fullWidth>
        {viewInv && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Receipt color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewInv.invoiceNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Purchase Invoice</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewInv.status} color={STATUS_COLOR[viewInv.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            {viewInv.status !== 'Paid' && viewInv.status !== 'Cancelled' && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
                px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Button size="small" variant="contained" startIcon={<Payment />}
                  onClick={() => {
                    setViewInv(null);
                    navigate('/payables/vouchers/new', { state: { invoiceNo: viewInv.invoiceNo, supplierCode: viewInv.supplierCode } });
                  }}>
                  Pay Now
                </Button>
              </Box>
            )}
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
              <InfoRow label="Invoice No"          value={viewInv.invoiceNo} />
              <InfoRow label="Purchase Order No"   value={viewInv.orderNo} />
              <InfoRow label="Invoice Date"        value={viewInv.invoiceDate ? new Date(viewInv.invoiceDate).toLocaleDateString() : ''} />
              <InfoRow label="Supplier Invoice No" value={viewInv.supplierInvoiceNo} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Supplier</Typography>
              <InfoRow label="Supplier Code" value={viewInv.supplierCode} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Financials</Typography>
              <InfoRow label={`Subtotal (${sym()})`}
                value={(viewInv.subtotal ?? (viewInv.lines || []).reduce((s, l) => s + (l.amount || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <InfoRow label={`VAT (${sym()})`}
                value={(viewInv.totalVAT ?? (viewInv.lines || []).reduce((s, l) => s + (l.vatAmount || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <InfoRow label={`Total Amount (${sym()})`}
                value={(viewInv.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <InfoRow label={`Amount Paid (${sym()})`}
                value={(viewInv.paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
                  Outstanding Balance
                </Typography>
                <Chip
                  label={`${sym()} ${fmt(viewInv.balance || 0)}`}
                  color={viewInv.balance > 0 ? 'error' : 'success'}
                  size="small" variant="outlined" sx={{ fontWeight: 700 }}
                />
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Invoice Lines */}
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory sx={{ fontSize: 18 }} /> Line Items ({viewInv.lines?.length || 0})
              </Typography>
              {viewInv.lines && viewInv.lines.length > 0 ? (
                <Table size="small" sx={{ mt: 1, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>Line</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>Type</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>Description</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>Qty</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>VAT</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, py: 0.8 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewInv.lines.map((line, idx) => (
                      <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.6 }}>{line.lineNo}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.6 }}>
                          <Chip label={line.lineType} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', py: 0.6, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }} title={line.description}>
                          {line.description}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}>{line.qty}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}>{(line.unitPrice || 0).toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}
                          title={line.vatPercent ? `${line.vatPercent}%` : ''}>
                          {line.vatAmount ? (line.vatAmount).toFixed(2) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.6, fontWeight: 600 }}>{(line.amount || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Subtotal / VAT / Total */}
                    {(() => {
                      const sub = viewInv.subtotal ?? viewInv.lines.reduce((s, l) => s + (l.amount || 0), 0);
                      const vat = viewInv.totalVAT ?? viewInv.lines.reduce((s, l) => s + (l.vatAmount || 0), 0);
                      const gross = viewInv.totalAmount ?? (sub + vat);
                      return (
                        <>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell colSpan={6} align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}>Subtotal</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}>{sub.toFixed(2)}</TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell colSpan={6} align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}>VAT</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', py: 0.6 }}>{vat.toFixed(2)}</TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell colSpan={6} align="right" sx={{ fontSize: '0.8rem', fontWeight: 700, py: 0.8 }}>TOTAL</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700, py: 0.8 }}>{gross.toFixed(2)}</TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>No line items</Typography>
              )}

              <Divider sx={{ my: 1.5 }} />
              <AttachmentsPanel refType="PurchaseInvoice" refId={viewInv.invoiceNo} />
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setViewInv(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PurchaseInvoiceList;
